import {
  type AuctionListing,
  type AuctionSort,
  type ClassId,
  getItem,
  type InventoryState,
  isBetterThanWorn,
  type ItemInstance,
  type ItemKind,
  type ItemSlot,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { AuthError } from '../net/session.js';
import { coin, formatGold, itemRow, type ItemRowParts, markIcon, upgradedName } from './bench.js';
import { compareWithWorn } from './item-compare.js';
import { describeOnHover, type HoverContext } from './item-hover.js';
import { addBetterArrow } from './item-icon.js';
import { uiFormat, uiText } from './text.js';

/**
 * What the auction house's views share (ADR 0035): the French labels of the
 * filters, how a remaining time is printed, and a lot's row, a small card of
 * the counters' kit (bench.ts), the same on every side.
 */

/** The slots one can search; companions are not sold here. */
export const AUCTION_SLOT_LABELS: Partial<Record<ItemSlot, string>> = {
  helmet: 'Casque',
  chest: 'Plastron',
  gloves: 'Gants',
  boots: 'Bottes',
  weapon: 'Arme',
  offhand: 'Main gauche',
  wings: 'Ailes',
  trail: 'Traînée',
};

export const KIND_LABELS: Record<ItemKind, string> = {
  equipment: 'Équipement',
  consumable: 'Consommable',
  material: 'Matériau',
};

export const SORT_LABELS: Record<AuctionSort, string> = {
  recent: 'Plus récents',
  price: 'Prix croissant',
  ending: 'Fin proche',
};

export { formatGold as formatPrice };

/** « 47 h 12 min », « 8 min », or « Terminé » once the date is past. */
export function timeLeft(expiresAt: string, now = Date.now()): string {
  const remaining = Date.parse(expiresAt) - now;
  if (Number.isNaN(remaining) || remaining <= 0) return uiText('Terminé');
  const minutes = Math.ceil(remaining / 60_000);
  const hours = Math.floor(minutes / 60);
  if (hours >= 1) {
    return uiFormat('{hours} h {minutes} min', {
      hours,
      minutes: String(minutes % 60).padStart(2, '0'),
    });
  }
  return uiFormat('{minutes} min', { minutes });
}

export interface Wearer {
  level: number;
  classId: ClassId;
}

export type Tone = 'success' | 'refused';

/** What every view of the house reads and calls: who asks, the bag, and the window's message line. */
export interface AuctionDesk {
  /** The character session's token: who buys and who sells. */
  token: string;
  /** Each operation asks the room for a fresh, proximity-checked ticket. */
  access: () => Promise<string>;
  icons: CssAtlas | null;
  wearer: () => Wearer;
  /** The bag and the purse, as the room last pushed them. */
  inventory: () => InventoryState | undefined;
  say: (text: string, tone: Tone) => void;
}

/** The item a lot holds, as the card and the icon read it. */
export function listingInstance(listing: AuctionListing): ItemInstance {
  return {
    id: listing.id,
    templateId: listing.templateId,
    quantity: listing.quantity,
    upgradeLevel: listing.upgradeLevel,
    bound: false,
    ...(listing.affixes !== undefined ? { affixes: listing.affixes } : {}),
  };
}

/** A price with the gold coin before it, at the right of a lot's row; red when the purse falls short. */
export function priceTag(price: number, short = false): HTMLElement {
  const tag = document.createElement('span');
  tag.className = short ? 'auction__price auction__price--short' : 'auction__price';
  tag.append(coin(), formatGold(price));
  return tag;
}

/**
 * A lot as a small card: its icon with the count and the smith's level, its
 * name in its rarity's colour, a line of details for the view to fill, and
 * its card on hover (a tap on a phone), set against the piece worn when
 * `equipped` is given.
 */
export function lotRow(
  listing: AuctionListing,
  icons: CssAtlas | null,
  wearer: () => Wearer,
  equipped?: () => InventoryState['equipped'] | undefined,
): ItemRowParts {
  const template = getItem(listing.templateId);
  const parts = itemRow(
    template,
    icons,
    upgradedName(template, listing.templateId, listing.upgradeLevel),
    listing.templateId,
  );
  parts.row.classList.add('auction__row');
  parts.row.dataset.listing = listing.id;
  markIcon(parts.icon, listing.quantity, listing.upgradeLevel);
  // Only where the lot is set against the piece worn: the lots for sale, not mine.
  if (
    template !== undefined &&
    equipped !== undefined &&
    isBetterThanWorn(
      { template, upgradeLevel: listing.upgradeLevel, affixes: listing.affixes },
      wearer(),
      equipped(),
    )
  ) {
    addBetterArrow(parts.icon);
  }
  const instance = listingInstance(listing);
  const context = (): HoverContext => {
    const compare =
      template === undefined || equipped === undefined
        ? undefined
        : compareWithWorn(template, equipped(), listing.upgradeLevel, listing.affixes);
    return { wearer: wearer(), details: compare === undefined ? {} : { compare }, instance };
  };
  describeOnHover(parts.slot, template, context);
  describeOnHover(parts.text, template, context);
  return parts;
}

/** What to tell the player when a call failed: the server's word, or the generic one. */
export function failureText(error: unknown): string {
  if (error instanceof AuthError) return error.message;
  return uiText('L’hôtel des ventes ne répond pas.');
}
